import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import { useDocuments, DocumentItem } from '../hooks/useDocuments';
import { ContentItem } from '../hooks/useDiaryItems';
import { ThemeProvider } from '@mui/material/styles';
import NodeTheme from '../resources/NodeTheme';

import FullSizePreview from './FullSizePreview';
import DocumentChat from './DocumentChat';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import { config } from '../config';
import Collapse from '@mui/material/Collapse';
import { DocumentDownloader } from '../util/downloadDocuments';
import { useImageUrls } from '../hooks/useImageUrls';
import type { ImageUrls } from '../hooks/useImageUrls';

type DateKey = {
  dayLabel?: 'unknown' | 'today' | 'yesterday';
  daysAgo?: number;
  month?: number;
  year?: number;
};

const getDateKey = (date: number | string | undefined): DateKey => {
  if (!date) return { dayLabel: 'unknown' };

  const itemDate = new Date(date);
  itemDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todayTime = today.getTime();
  const itemTime = itemDate.getTime();

  const daysDiff = Math.floor((todayTime - itemTime) / (24 * 60 * 60 * 1000));

  if (daysDiff === 0) {
    return { dayLabel: 'today' };
  }

  if (daysDiff === 1) {
    return { dayLabel: 'yesterday' };
  }

  if (daysDiff <= 7) {
    return { daysAgo: daysDiff };
  }

  return { month: itemDate.getMonth() + 1, year: itemDate.getFullYear() };
};

const getDocumentDate = (item: DocumentItem): number | string | undefined => {
  return (
    item.paymentDate ||
    item.endDate ||
    item.startDate ||
    item.createdAt ||
    item.created_at ||
    item.timestamp
  );
};

const FilesTable = () => {
  const { t, i18n } = useTranslation();
  const { documents, isLoading, refetch } = useDocuments();
  const [activeOrganisation] = useActiveOrganisation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoadingFullSize, setIsLoadingFullSize] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DocumentItem | null>(null);
  const [localCategories, setLocalCategories] = useState<{
    [id: string]: string | null;
  }>({});
  const [selectedThreadId, setSelectedThreadId] = useState<
    string | undefined
  >();
  const [selectedFileForChat, setSelectedFileForChat] =
    useState<DocumentItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    month: '',
    year: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<
    'filtered' | 'group' | 'file' | null
  >(null);
  const [exportData, setExportData] = useState<{
    files: DocumentItem[];
    group?: { key: string; label: string; items: DocumentItem[] };
  } | null>(null);

  // Update local categories when documents change
  useEffect(() => {
    const newLocalCategories = documents.reduce(
      (acc, file) => {
        acc[file.id] = file.category ?? '';
        return acc;
      },
      {} as { [id: string]: string }
    );
    setLocalCategories(newLocalCategories);
  }, [documents]);

  // Use useRef instead of Map for storing refs
  const monthGroupRefs = useRef<{ [key: string]: HTMLElement }>({});

  // Add scroll handler
  const scrollToMonth = useCallback(
    (targetMonth: number, targetYear: number) => {
      // Get all groups and find the most relevant one
      const targetDate = new Date(targetYear, targetMonth);
      targetDate.setHours(0, 0, 0, 0);

      // Get all groups that contain documents from the target month
      const relevantGroups = Object.entries(monthGroupRefs.current)
        .map(([key, element]) => {
          try {
            const dateKey = JSON.parse(key);

            // For month-year groups, check direct match
            if (
              dateKey.month === targetMonth + 1 &&
              dateKey.year === targetYear
            ) {
              return { key, element, date: targetDate };
            }

            // For day-based groups (today, yesterday, N days ago), check their actual dates
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            let groupDate;

            if (dateKey.dayLabel === 'today') {
              groupDate = today;
            } else if (dateKey.dayLabel === 'yesterday') {
              groupDate = new Date(today);
              groupDate.setDate(today.getDate() - 1);
            } else if (dateKey.daysAgo !== undefined) {
              groupDate = new Date(today);
              groupDate.setDate(today.getDate() - dateKey.daysAgo);
            }

            if (
              groupDate &&
              groupDate.getMonth() === targetMonth &&
              groupDate.getFullYear() === targetYear
            ) {
              return { key, element, date: groupDate };
            }
          } catch (error) {
            console.error('Error parsing date key:', error);
          }
          return null;
        })
        .filter(
          (group): group is { key: string; element: HTMLElement; date: Date } =>
            group !== null
        )
        // Sort by date descending (most recent first)
        .sort((a, b) => b.date.getTime() - a.date.getTime());

      // Get the most recent group from the target month
      const mostRecentGroup = relevantGroups[0];

      if (mostRecentGroup?.element) {
        // Offset is HeaderBar height (84.5px) + margin (16px) - shadow (3px)
        const headerOffset = 97.5;
        const elementPosition =
          mostRecentGroup.element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - headerOffset,
          behavior: 'smooth',
        });
      }
    },
    []
  );

  const monthNames = useMemo(
    () => [
      t('January'),
      t('February'),
      t('March'),
      t('April'),
      t('May'),
      t('June'),
      t('July'),
      t('August'),
      t('September'),
      t('October'),
      t('November'),
      t('December'),
    ],
    [t]
  );

  // Filter out files without content
  const filesWithContent = useMemo(
    () => documents.filter((file) => file.content?.[0]),
    [documents]
  );

  // Prepare input for useImageUrls
  const imageSources = useMemo(
    () =>
      documents
        .map((file) => {
          const content = file.content?.[0];
          if (!content || !content.hash) return null;
          return {
            id: file.id,
            hash: content.hash,
            path: content.path,
            thumbnail: content.thumbnail,
            s3Url: file.s3Url || (content as any).s3Url,
          };
        })
        .filter(Boolean) as Array<{
        id: string;
        hash: string;
        path?: string;
        thumbnail?: string;
        s3Url?: string;
      }>,
    [documents]
  );

  // Use the shared hook for image loading
  const imageUrls: ImageUrls & { loadFullSize?: Function } =
    useImageUrls(imageSources);

  // Memoize the getValidDate function to avoid recreating it on every render
  const getValidDate = useCallback((file: DocumentItem) => {
    const timestamp = getDocumentDate(file);
    if (!timestamp) return new Date();
    const timestampNum =
      typeof timestamp === 'number' ? timestamp : Number(timestamp);
    if (timestampNum && timestampNum.toString().length === 10) {
      return new Date(timestampNum * 1000);
    }
    return new Date(timestamp);
  }, []);

  // Get years and months from files (needed for filter dropdowns)
  const years = useMemo(
    () =>
      [
        ...new Set(
          filesWithContent.map((file) => getValidDate(file).getFullYear())
        ),
      ].sort((a, b) => b - a),
    [filesWithContent, getValidDate]
  );
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  // Filter and search logic - memoized for performance
  const filteredFiles = useMemo(() => {
    let filtered = [...filesWithContent];

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const currentLanguage = i18n.language || 'en';

      filtered = filtered.filter((item) => {
        const itemDate = getValidDate(item);

        // Search priority hierarchy based on actual item structure
        const searchPriority = [
          item.title?.toLowerCase() || '',
          item.description?.toLowerCase() || '',
          item.lastAssistantMessage?.toLowerCase() || '',
          (typeof item.interpretationObj === 'object' &&
            item.interpretationObj?.description?.toLowerCase()) ||
            '',
          item.category?.toLowerCase() || '',
          // Search through content array for text content
          ...(item.content
            ?.filter((content: ContentItem) => content.type === 'text')
            .map((content: ContentItem) => content.text?.toLowerCase() || '') ||
            []),
          // Include the date in a readable format
          itemDate.toLocaleDateString(currentLanguage, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          }),
          // Include the state
          item.state?.toLowerCase() || '',
          // Include the status if exists
          item.status?.toLowerCase() || '',
        ];

        return searchPriority.some((text) => text.includes(query));
      });
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter((file) =>
        filters.category === 'other'
          ? !file.category
          : file.category === filters.category
      );
    }
    // Month filter
    if (filters.month) {
      filtered = filtered.filter(
        (file) => getValidDate(file).getMonth() + 1 === Number(filters.month)
      );
    }
    // Year filter
    if (filters.year) {
      filtered = filtered.filter(
        (file) => getValidDate(file).getFullYear() === Number(filters.year)
      );
    }
    return filtered;
  }, [filesWithContent, searchQuery, filters, i18n.language, getValidDate]);

  // Helper function to get the primary date for a file
  const getFileDate = useCallback((file: DocumentItem): number => {
    const date = getDocumentDate(file);
    if (typeof date === 'string') {
      return new Date(date).getTime();
    }
    return date || 0;
  }, []);

  // Helper function to get date-sorted files with threads
  const getDateSortedFilesWithThreads = useCallback(
    (files: DocumentItem[]): DocumentItem[] => {
      return files
        .filter((file) => file.threadId)
        .sort((a, b) => getFileDate(b) - getFileDate(a)); // Sort by date descending (newest first)
    },
    [getFileDate]
  );

  // Get date-sorted files with threads
  const dateSortedFilesWithThreads = useMemo(
    () => getDateSortedFilesWithThreads(filteredFiles),
    [filteredFiles, getDateSortedFilesWithThreads]
  );

  // Find current index in the date-sorted array
  const currentChatIndex = selectedFileForChat
    ? dateSortedFilesWithThreads.findIndex(
        (file) => file.threadId === selectedFileForChat.threadId
      )
    : -1;

  const handlePreviousChat = useCallback(() => {
    if (currentChatIndex > 0) {
      const previousFile = dateSortedFilesWithThreads[currentChatIndex - 1];
      setSelectedFileForChat(previousFile);
      setSelectedThreadId(previousFile.threadId);
    }
  }, [currentChatIndex, dateSortedFilesWithThreads]);

  const handleNextChat = useCallback(() => {
    if (currentChatIndex < dateSortedFilesWithThreads.length - 1) {
      const nextFile = dateSortedFilesWithThreads[currentChatIndex + 1];
      setSelectedFileForChat(nextFile);
      setSelectedThreadId(nextFile.threadId);
    }
  }, [currentChatIndex, dateSortedFilesWithThreads]);

  // Move getGroupedFiles above its first usage
  const getGroupedFiles = useCallback(
    (files: DocumentItem[]) => {
      const grouped = files.reduce(
        (
          acc: {
            [key: string]: {
              key: string;
              label: string;
              items: DocumentItem[];
            };
          },
          file: DocumentItem
        ) => {
          const dateKey = getDateKey(getDocumentDate(file));
          const keyString = JSON.stringify(dateKey);
          let label = '';
          if (dateKey.dayLabel === 'today') label = t('Today');
          else if (dateKey.dayLabel === 'yesterday') label = t('Yesterday');
          else if (dateKey.dayLabel === 'unknown') label = t('Unknown Date');
          else if (dateKey.daysAgo !== undefined)
            label = `${dateKey.daysAgo} ${t('days ago')}`;
          else if (dateKey.month !== undefined && dateKey.year !== undefined)
            label = `${monthNames[dateKey.month - 1]} ${dateKey.year}`;
          if (!acc[keyString]) {
            acc[keyString] = {
              key: keyString,
              label,
              items: [file],
            };
          } else {
            acc[keyString].items.push(file);
          }
          return acc;
        },
        {}
      );
      return Object.values(grouped).sort((a, b) => {
        const aFirstFile = a.items[0];
        const bFirstFile = b.items[0];
        const aDate = getValidDate(aFirstFile);
        const bDate = getValidDate(bFirstFile);
        return bDate.getTime() - aDate.getTime();
      });
    },
    [t, monthNames, getValidDate]
  );

  const handleResetFilters = useCallback(() => {
    setFilters({ category: '', month: '', year: '' });
  }, []);

  const isFiltered =
    filters.category !== '' || filters.month !== '' || filters.year !== '';
  const hasFiles = filteredFiles.length > 0;

  const handleImageOpen = useCallback(
    async (file: DocumentItem) => {
      const content = file.content?.[0];
      if (!content?.path || !content?.hash) return;

      setSelectedFile(file);
      setSelectedImage(imageUrls[file.id]?.thumbnail || null);

      if (!imageUrls[file.id]?.fullSize) {
        setIsLoadingFullSize(true);
        if (typeof imageUrls.loadFullSize === 'function') {
          try {
            const url = await imageUrls.loadFullSize({
              id: file.id,
              hash: content.hash,
              path: content.path,
              thumbnail: content.thumbnail,
              s3Url: file.s3Url || (content as any).s3Url,
            });
            if (url) {
              setSelectedImage(url);
            }
          } catch (error) {
            console.error('Error loading full size image:', error);
          }
        }
        setIsLoadingFullSize(false);
      } else {
        setSelectedImage(imageUrls[file.id]?.fullSize);
      }
    },
    [imageUrls]
  );

  const handleImageClose = useCallback(() => {
    setSelectedImage(null);
    setSelectedFile(null);
  }, []);

  const isPDF = useCallback((path?: string): boolean => {
    if (!path) return false;
    return path.toLowerCase().endsWith('.pdf');
  }, []);

  const categories = useMemo(
    () => [
      {
        id: 'expense',
        label: t('expense'),
        icon: <CreditCardOutlinedIcon sx={{ fontSize: 20 }} />,
      },
      {
        id: 'income',
        label: t('income'),
        icon: <PaymentsOutlinedIcon sx={{ fontSize: 20 }} />,
      },
      {
        id: 'bank statement',
        label: t('bank statement'),
        icon: <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />,
      },
      {
        id: 'document',
        label: t('document'),
        icon: <FolderOutlinedIcon sx={{ fontSize: 20 }} />,
      },
      {
        id: '',
        label: t('no category'),
        icon: <HelpOutlineIcon sx={{ fontSize: 20 }} />,
      },
    ],
    [t]
  );

  const updateDocumentCategory = useCallback(
    async (
      documentId: string,
      newCategory: string | null,
      type: 'image' | 'diary' = 'image' // Default to image for new behavior
    ): Promise<boolean> => {
      try {
        // Update local state immediately
        setLocalCategories((prev) => ({
          ...prev,
          [documentId]: newCategory,
        }));

        if (!activeOrganisation?.organizationId) {
          console.error('Missing organization ID');
          // Revert local state on error
          setLocalCategories((prev) => ({
            ...prev,
            [documentId]:
              documents.find((d) => d.id === documentId)?.category || null,
          }));
          return false;
        }

        // Get access token
        const tokenResponse = await fetch('/access-token');
        const { accessToken } = await tokenResponse.json();

        // Choose endpoint based on type
        const endpoint =
          type === 'image'
            ? config.endpoints.documentsCategorySet
            : config.endpoints.diaryCategorySet;

        // Prepare request body based on type
        const body =
          type === 'image'
            ? {
                imageId: documentId,
                category: newCategory,
              }
            : {
                diaryId: documentId,
                category: newCategory,
              };

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-organization': activeOrganisation.organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          console.error(`Error updating category: ${response.status}`);
          // Revert local state on error
          setLocalCategories((prev) => ({
            ...prev,
            [documentId]:
              documents.find((d) => d.id === documentId)?.category || null,
          }));
          return false;
        }

        // Refetch the files to update the UI
        await refetch();
        return true;
      } catch (error) {
        console.error('Error updating document category:', error);
        // Revert local state on error
        setLocalCategories((prev) => ({
          ...prev,
          [documentId]:
            documents.find((d) => d.id === documentId)?.category || null,
        }));
        return false;
      }
    },
    [activeOrganisation?.organizationId, documents, refetch]
  );

  // Add a handler for refetch with isRefetching state
  const handleRefetch = useCallback(async () => {
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  }, [refetch]);

  // Add robust download logic
  const handleExportFiltered = useCallback(() => {
    setExportType('filtered');
    setExportData({ files: filteredFiles });
    setExportModalOpen(true);
  }, [filteredFiles]);

  const handleExportGroup = useCallback(
    (group: { key: string; label: string; items: DocumentItem[] }) => {
      setExportType('group');
      setExportData({ files: group.items, group });
      setExportModalOpen(true);
    },
    []
  );

  const handleExportFile = useCallback((file: DocumentItem) => {
    setExportType('file');
    setExportData({ files: [file] });
    setExportModalOpen(true);
  }, []);

  const handleCancelExport = useCallback(() => {
    setExportModalOpen(false);
    setExportType(null);
    setExportData(null);
  }, []);

  // Helper function to generate consistent file suffixes
  const generateFileSuffix = useCallback(
    (
      exportType: string | null,
      exportData: any,
      isFiltered: boolean,
      searchQuery: string
    ): string => {
      if (exportType === 'filtered') {
        return isFiltered || searchQuery ? 'filtered-files' : 'all-files';
      } else if (exportType === 'group' && exportData?.group) {
        return exportData.group.label
          .toLowerCase()
          .replace(/[^a-z0-9äöåéèêëàâïîìôòóùûüÿñç]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      } else if (exportType === 'file' && exportData?.files?.length === 1) {
        const file = exportData.files[0];
        return file.title
          ? file.title
              .toLowerCase()
              .replace(/[^a-z0-9äöåéèêëàâïîìôòóùûüÿñç]/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '')
          : 'file';
      }
      return 'exported-files';
    },
    []
  );

  const handleDownloadZip = useCallback(async () => {
    if (!activeOrganisation?.organizationId || !exportData) return;

    setExportModalOpen(false);

    try {
      // Get access token
      const tokenResponse = await fetch('/access-token');
      const { accessToken } = await tokenResponse.json();

      // Get all thread IDs from files
      const threadIds = exportData.files
        .map((file) => file.threadId)
        .filter((id): id is string => Boolean(id));

      if (threadIds.length === 0) {
        console.warn('No valid thread IDs found for download');
        return;
      }

      const fileSuffix = generateFileSuffix(
        exportType,
        exportData,
        isFiltered,
        searchQuery
      );

      await DocumentDownloader.downloadDocuments({
        accessToken,
        organizationId: activeOrganisation.organizationId,
        threadIds,
        fileSuffix,
        plural: exportType !== 'file',
      });
    } catch (e) {
      console.error(`Failed to download files: ${e}`);
    } finally {
      setExportType(null);
      setExportData(null);
    }
  }, [
    activeOrganisation?.organizationId,
    exportData,
    exportType,
    isFiltered,
    searchQuery,
    generateFileSuffix,
  ]);

  const handleSendEmail = useCallback(async () => {
    if (!activeOrganisation?.organizationId || !exportData)
      throw new Error('No active organisation or export data');

    // Close the dialog immediately
    setExportModalOpen(false);
    setExportType(null);
    setExportData(null);

    try {
      // Get access token
      const tokenResponse = await fetch('/access-token');
      const { accessToken } = await tokenResponse.json();

      // Get current user's email
      const userResponse = await fetch('/whoami');
      const userData = await userResponse.json();
      const userEmail = userData.email;

      if (!userEmail) {
        console.error('No email found for current user');
        return;
      }

      // Get all thread IDs from files
      const threadIds = exportData.files
        .map((file) => file.threadId)
        .filter((id): id is string => Boolean(id));

      if (threadIds.length === 0) {
        console.warn('No valid thread IDs found for email');
        return;
      }

      // Determine email body content based on export type
      let requestBody: any = {
        threadIds,
        email: userEmail,
        fileSuffix: generateFileSuffix(
          exportType,
          exportData,
          isFiltered,
          searchQuery
        ),
      };

      // Call the email documents API
      const response = await fetch(config.endpoints.emailDocuments, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'x-organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to send email:', error);
        return;
      }

      const result = await response.json();
      console.log('Email sent successfully:', result);
    } catch (error) {
      console.error('Error sending email:', error);
    }
  }, [
    activeOrganisation?.organizationId,
    exportData,
    exportType,
    isFiltered,
    searchQuery,
    generateFileSuffix,
  ]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={NodeTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          visibility: isLoading ? 'hidden' : 'visible',
          mx: { xs: 2, sm: 3 },
        }}
      >
        {/* Search Bar and Filters Button */}
        <Box
          sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('Search files...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#382767' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(56, 39, 103, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(56, 39, 103, 0.2)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#382767',
                },
              },
            }}
          />
          <Button
            variant="floating"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              height: '56px',
              minWidth: '48px',
              px: 1,
              '& .MuiButton-startIcon': {
                margin: 0,
              },
              '@media (min-width: 450px)': {
                minWidth: '120px',
                px: 2,
                '& .MuiButton-startIcon': {
                  margin: '0 8px 0 0',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'none',
                '@media (min-width: 450px)': {
                  display: 'block',
                },
              }}
            >
              {t('Filters')}
            </Box>
          </Button>
        </Box>
        {/* Filters Section */}
        <Collapse in={showFilters} unmountOnExit>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              backgroundColor: '#F8F8FB',
              p: 2,
              borderRadius: '8px',
              border: '1px solid rgba(56, 39, 103, 0.1)',
            }}
          >
            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Category')}</InputLabel>
              <Select
                value={filters.category}
                label={t('Category')}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <MenuItem value="">{t('All Categories')}</MenuItem>
                {categories
                  .filter((cat) => cat.id !== '')
                  .map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {t(cat.label || '')}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Month')}</InputLabel>
              <Select
                value={filters.month}
                label={t('Month')}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, month: e.target.value }))
                }
              >
                <MenuItem value="">{t('All Months')}</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {monthNames[month - 1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>{t('Year')}</InputLabel>
              <Select
                value={filters.year}
                label={t('Year')}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, year: e.target.value }))
                }
              >
                <MenuItem value="">{t('All Years')}</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isFiltered && (
              <Box sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                <Box>
                  <Tooltip title={t('Reset all filters')}>
                    <Button
                      variant="floating"
                      startIcon={<RestartAltIcon />}
                      onClick={handleResetFilters}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                      }}
                    >
                      {t('Reset')}
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            justifyContent: 'flex-end',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box
            sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}
          >
            <Tooltip title={t('Refresh')}>
              <span style={{ width: '100%' }}>
                <Button
                  variant="floating"
                  fullWidth={true}
                  startIcon={
                    isRefetching ? (
                      <CircularProgress size={20} sx={{ color: '#382767' }} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={handleRefetch}
                  disabled={isRefetching}
                  sx={{
                    width: '100%',
                    '&:hover': {
                      transform: isRefetching ? 'none' : 'translateY(-2px)',
                    },
                  }}
                >
                  {isRefetching ? t('Refreshing...') : t('Refresh')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                isFiltered || searchQuery
                  ? t('Export selected')
                  : t('Export all')
              }
            >
              <span style={{ width: '100%' }}>
                <Button
                  variant="floating"
                  fullWidth={true}
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportFiltered}
                  disabled={!hasFiles}
                  sx={{
                    width: '100%',
                    '&:hover': {
                      transform: !hasFiles ? 'none' : 'translateY(-2px)',
                    },
                  }}
                >
                  {isFiltered || searchQuery
                    ? t('Export Selected')
                    : t('Export All')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        {/* Results count */}
        {(isFiltered || searchQuery) && (
          <Typography
            variant="body2"
            sx={{
              visibility: isLoading ? 'hidden' : 'visible',
              px: { xs: 2, sm: 3 },
              py: 1,
              color: '#666',
            }}
          >
            {`${t('Showing')} ${filteredFiles.length} ${filteredFiles.length === 1 ? t('1-result') : t('n-results')}`}
          </Typography>
        )}
      </Box>

      {/* Month Quick Filters */}
      <Box
        sx={{
          visibility: isLoading ? 'hidden' : 'visible',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            width: { xs: 'max-content', sm: '100%' },
          }}
        >
          {Array.from(
            new Set(
              filteredFiles.map((file) => {
                const date = getValidDate(file);
                return `${date.getMonth()}-${date.getFullYear()}`;
              })
            )
          )
            .sort((a, b) => {
              const [monthA, yearA] = a.split('-').map(Number);
              const [monthB, yearB] = b.split('-').map(Number);
              return yearB - yearA || monthB - monthA;
            })
            .map((monthYear) => {
              const [month, year] = monthYear.split('-').map(Number);
              return (
                <Button
                  key={monthYear}
                  variant="floating"
                  size="small"
                  onClick={() => scrollToMonth(month, year)}
                  sx={{
                    flex: '0 0 auto',
                    px: 1.5,
                    py: 0.5,
                    minHeight: '32px',
                    fontSize: '0.875rem',
                  }}
                >
                  {`${monthNames[month]} ${year}`}
                </Button>
              );
            })}
        </Box>
      </Box>

      {/* File grid rendering */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          m: { xs: 2, sm: 3 },
        }}
      >
        {getGroupedFiles(filteredFiles).map((group) => (
          <React.Fragment key={group.key}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                mt: 2,
                mb: 1,
              }}
            >
              <Typography
                ref={(el: HTMLHeadingElement | null) => {
                  if (el) {
                    monthGroupRefs.current[group.key] = el;
                  }
                }}
                variant="body1"
                sx={{
                  color: '#444',
                  fontWeight: 400,
                  fontSize: '1.125rem',
                  flex: 1,
                }}
              >
                {group.label}
              </Typography>
              <Tooltip title={t('Export group')}>
                <span>
                  <Button
                    variant="floating"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => handleExportGroup(group)}
                    sx={{
                      minWidth: '120px',
                    }}
                  >
                    {t('Export Group')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {group.items.map((file) => (
              <Box
                key={file.id}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                  width: { xs: '145px', sm: '180px' },
                  boxSizing: 'border-box',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.3s ease',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    transition: 'transform 0.1s ease',
                  },
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    aspectRatio: '1/1',
                    border: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    boxShadow: 2,
                    background: '#fff',
                    p: 0,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#F8F8FBBF',
                      boxShadow: '0 2px 3px rgba(0,0,0,0.4)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      backgroundColor: '#F8F8FB7F',
                      boxShadow: 0,
                    },
                  }}
                >
                  {/* Floating action buttons in top right */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <Tooltip title={t('View full size')}>
                      <span>
                        <IconButton
                          sx={{
                            width: { xs: 28, sm: 32, md: 36 },
                            height: { xs: 28, sm: 32, md: 36 },
                            backgroundColor: 'rgba(255, 255, 255, 0.92)',
                            '&:hover': {
                              backgroundColor: 'white',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageOpen(file);
                          }}
                        >
                          <ZoomInIcon
                            sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('Export file')}>
                      <span>
                        <IconButton
                          sx={{
                            width: { xs: 28, sm: 32, md: 36 },
                            height: { xs: 28, sm: 32, md: 36 },
                            backgroundColor: 'rgba(255, 255, 255, 0.92)',
                            '&:hover': {
                              backgroundColor: 'white',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportFile(file);
                          }}
                        >
                          <DownloadIcon
                            sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {/* Image area with clickable overlay for chat */}
                  <Box
                    sx={{
                      position: 'relative',
                      flex: '1 1 0%',
                      minHeight: 0,
                      width: '100%',
                      overflow: 'hidden',
                      p: 0,
                      m: 0,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1,
                      }}
                      onClick={() => {
                        if (file.threadId) {
                          setSelectedThreadId(file.threadId);
                          setSelectedFileForChat(file);
                        } else {
                          console.log('File has no threadId, not opening chat');
                        }
                      }}
                    >
                      {!imageUrls[file.id] ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.6 },
                              '50%': { opacity: 0.8 },
                              '100%': { opacity: 0.6 },
                            },
                          }}
                        >
                          <CircularProgress
                            size={40}
                            sx={{ color: 'rgba(0, 0, 0, 0.2)' }}
                          />
                        </Box>
                      ) : (
                        <img
                          src={imageUrls[file.id]?.thumbnail || ''}
                          alt={file.title}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'all 0.3s ease',
                            opacity: imageUrls[file.id]?.thumbnail ? 1 : 0,
                            animation: imageUrls[file.id]?.thumbnail
                              ? 'fadeIn 0.5s ease-in-out forwards'
                              : 'none',
                            display: imageUrls[file.id]?.failed
                              ? 'none'
                              : 'block',
                            cursor: 'pointer',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  {/* Category select at the bottom, always visible */}
                  <Box
                    sx={{
                      width: '100%',
                      background: 'rgba(0,0,0,0.8)',
                      p: 0,
                      m: 0,
                      position: 'relative',
                      zIndex: 3,
                      display: 'block !important',
                      visibility: 'visible !important',
                    }}
                  >
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        m: 0,
                        p: 0,
                        display: 'block !important',
                        visibility: 'visible !important',
                      }}
                    >
                      <Select
                        displayEmpty
                        value={localCategories[file.id] ?? ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          const value = e.target.value;

                          // Use source property to determine which API to use, similar to mobile app
                          const type = file.source === 'v2' ? 'image' : 'diary';
                          const idToUse =
                            type === 'image' ? file.id : file.diaryId;

                          if (!idToUse) {
                            console.error('No valid ID found for file:', file);
                            return;
                          }

                          updateDocumentCategory(idToUse, value, type);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'block !important',
                          visibility: 'visible !important',
                          opacity: 1,
                          backgroundColor: 'transparent !important',
                          color: 'white !important',
                          '& .MuiSelect-select': {
                            display: 'flex !important',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            color: 'white !important',
                            backgroundColor: 'transparent !important',
                            borderRadius: 0,
                            visibility: 'visible !important',
                            opacity: 1,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none !important',
                            borderRadius: 0,
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none !important',
                            borderRadius: 0,
                          },
                          '& .MuiSelect-icon': {
                            color: 'white !important',
                            visibility: 'visible !important',
                            opacity: 1,
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'white !important',
                            visibility: 'visible !important',
                            opacity: 1,
                          },
                          borderRadius: 0,
                          '& .MuiSelect-select.MuiSelect-select': {
                            visibility: 'visible !important',
                            opacity: 1,
                          },
                          '& .MuiInputBase-root': {
                            visibility: 'visible !important',
                            opacity: 1,
                            backgroundColor: 'transparent !important',
                          },
                          '&:hover': {
                            backgroundColor: 'transparent !important',
                            '& .MuiSelect-select': {
                              visibility: 'visible !important',
                              opacity: 1,
                            },
                            '& .MuiSelect-icon': {
                              visibility: 'visible !important',
                              opacity: 1,
                            },
                          },
                          '& .MuiSelect-select:not([data-mui-select-disabled])':
                            {
                              visibility: 'visible !important',
                              opacity: 1,
                            },
                        }}
                        renderValue={(value: string) => {
                          const category = categories.find(
                            (cat) => cat.id === value
                          );
                          if (category) {
                            return (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  height: '100%',
                                  visibility: 'visible',
                                  opacity: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    alignItems: 'center',
                                  }}
                                >
                                  {category.icon}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    visibility: 'visible',
                                    opacity: 1,
                                  }}
                                >
                                  {t(category.label || '')}
                                </Typography>
                              </Box>
                            );
                          } else {
                            // Fallback for no category
                            return (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  height: '100%',
                                  visibility: 'visible',
                                  opacity: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    alignItems: 'center',
                                  }}
                                >
                                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    visibility: 'visible',
                                    opacity: 1,
                                  }}
                                >
                                  {t('no category')}
                                </Typography>
                              </Box>
                            );
                          }
                        }}
                      >
                        {categories
                          .filter((cat) => cat.id !== '')
                          .map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  height: '100%',
                                }}
                              >
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  {category.icon}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  {t(category.label || '')}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>

      <FullSizePreview
        open={!!selectedImage}
        onClose={handleImageClose}
        imageUrl={selectedImage}
        isLoading={isLoadingFullSize}
        isPDF={
          selectedFile?.content?.[0]?.path
            ? isPDF(selectedFile.content?.[0]?.path)
            : false
        }
      />

      <DocumentChat
        threadId={selectedThreadId}
        onClose={() => {
          setSelectedThreadId(undefined);
          setSelectedFileForChat(null);
        }}
        open={!!selectedThreadId}
        title={selectedFileForChat?.title}
        onPreviousChat={handlePreviousChat}
        onNextChat={handleNextChat}
        hasPreviousChat={currentChatIndex > 0}
        hasNextChat={currentChatIndex < dateSortedFilesWithThreads.length - 1}
      />

      {/* Export Options Modal */}
      <Dialog
        open={exportModalOpen}
        onClose={handleCancelExport}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: '#fff',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#382767',
            fontWeight: 600,
            fontSize: '1.25rem',
            pb: 1,
          }}
        >
          {t('Export Options')}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              mb: 1,
            }}
          >
            {t('Choose how you want to receive the file')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            px: 3,
            pb: 3,
            justifyContent: { xs: 'center', sm: 'space-between' },
            alignItems: { xs: 'center', sm: 'center' },
          }}
        >
          <Button
            variant="text"
            onClick={handleCancelExport}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              order: { xs: 3, sm: 1 }, // Cancel button goes to bottom on mobile
            }}
          >
            {t('Cancel')}
          </Button>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
              order: { xs: 1, sm: 2 }, // Action buttons go to top on mobile
              alignItems: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <Button
              variant="contained"
              onClick={handleDownloadZip}
              startIcon={<FileDownloadIcon />}
              sx={{
                backgroundColor: '#382767',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#2a1d4a',
                },
                borderRadius: 2,
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '280px', sm: 'none' },
              }}
            >
              {t('Download Zip')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSendEmail}
              startIcon={<EmailIcon />}
              sx={{
                backgroundColor: '#382767',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#2a1d4a',
                },
                borderRadius: 2,
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: '280px', sm: 'none' },
              }}
            >
              {t('Send by Email')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default FilesTable;
